/**
 * The logistics artwork behind the login screens: a network of cities joined by live routes, and a
 * truck driving along the footer.
 *
 * Hand-drawn SVG + CSS keyframes rather than a Lottie/animation library — the login screen is the
 * one page every user waits on before anything is cached, so it must not pull a dependency for
 * decoration. Everything here is inert markup: no state, no timers, nothing that can fail and take
 * the sign-in form down with it.
 *
 * Every animation is disabled under `prefers-reduced-motion` (see globals.css) — a map that pulses
 * forever is exactly what that setting exists to stop.
 */

/** Cities, positioned on a 900×600 viewBox roughly the shape of the network we actually run. */
const NODES: { x: number; y: number; label: string; hub?: boolean }[] = [
  { x: 300, y: 120, label: 'Ludhiana' },
  { x: 360, y: 190, label: 'Delhi NCR', hub: true },
  { x: 250, y: 250, label: 'Jaipur' },
  { x: 470, y: 175, label: 'Lucknow' },
  { x: 620, y: 235, label: 'Kolkata', hub: true },
  { x: 300, y: 360, label: 'Mumbai', hub: true },
  { x: 430, y: 330, label: 'Nagpur' },
  { x: 380, y: 460, label: 'Bengaluru', hub: true },
  { x: 470, y: 430, label: 'Hyderabad' },
  { x: 430, y: 530, label: 'Chennai' },
  { x: 300, y: 520, label: 'Kochi' },
];

/** Lanes between them. `d` is a quadratic curve so routes arc like flight paths, not spider webs. */
const LANES: { from: number; to: number; bend: number }[] = [
  { from: 0, to: 1, bend: -18 },
  { from: 1, to: 2, bend: 16 },
  { from: 1, to: 3, bend: -20 },
  { from: 3, to: 4, bend: -26 },
  { from: 1, to: 5, bend: 30 },
  { from: 5, to: 6, bend: -18 },
  { from: 6, to: 4, bend: -24 },
  { from: 5, to: 7, bend: 26 },
  { from: 6, to: 8, bend: 14 },
  { from: 7, to: 8, bend: -16 },
  { from: 7, to: 9, bend: -14 },
  { from: 7, to: 10, bend: 18 },
  { from: 4, to: 8, bend: 30 },
];

/** Quadratic path with the control point pushed perpendicular to the line by `bend`. */
function lanePath(a: { x: number; y: number }, b: { x: number; y: number }, bend: number) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const cx = mx + (-dy / len) * bend;
  const cy = my + (dx / len) * bend;
  return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
}

export function ConnectingDotsMap({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 900 600" className={className} aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="lg-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="lg-lane" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0.15" />
        </linearGradient>
      </defs>

      {/* Faint grid — reads as a map surface without pretending to be a real one. */}
      <g opacity="0.14" stroke="#fff" strokeWidth="0.6">
        {Array.from({ length: 15 }, (_, i) => <line key={`v${i}`} x1={i * 64} y1="0" x2={i * 64} y2="600" />)}
        {Array.from({ length: 10 }, (_, i) => <line key={`h${i}`} x1="0" y1={i * 64} x2="900" y2={i * 64} />)}
      </g>

      <ellipse cx="390" cy="320" rx="330" ry="290" fill="url(#lg-glow)" />

      {/* Lanes: a static hairline with a moving dash on top, so the route reads as live. */}
      <g fill="none" strokeLinecap="round">
        {LANES.map((l, i) => {
          const d = lanePath(NODES[l.from], NODES[l.to], l.bend);
          return (
            <g key={i}>
              <path d={d} stroke="#fff" strokeOpacity="0.18" strokeWidth="1.2" />
              <path d={d} stroke="url(#lg-lane)" strokeWidth="1.8" strokeDasharray="10 190"
                className="lg-lane" style={{ animationDelay: `${(i % 7) * 0.9}s` }} />
              {/* The parcel itself, riding the same curve. */}
              <circle r="3" fill="#fff" className="lg-parcel">
                <animateMotion dur={`${7 + (i % 5)}s`} begin={`${(i % 6) * 1.3}s`} repeatCount="indefinite" path={d} />
              </circle>
            </g>
          );
        })}
      </g>

      {/* Cities. Hubs get a second, slower ring so the eye lands on them first. */}
      <g>
        {NODES.map((n, i) => (
          <g key={n.label}>
            <circle cx={n.x} cy={n.y} r={n.hub ? 6 : 3.5} fill="#fff" fillOpacity={n.hub ? 0.95 : 0.7} />
            <circle cx={n.x} cy={n.y} r={n.hub ? 6 : 3.5} fill="none" stroke="#fff" strokeWidth="1"
              className="lg-ping" style={{ animationDelay: `${i * 0.55}s`, animationDuration: n.hub ? '3.2s' : '4.4s' }} />
            {n.hub && (
              <text x={n.x + 12} y={n.y + 4} fill="#fff" fillOpacity="0.75" fontSize="11" fontFamily="inherit">
                {n.label}
              </text>
            )}
          </g>
        ))}
      </g>
    </svg>
  );
}

/**
 * The footer road: a truck driving left to right, wheels turning, on a moving dashed lane.
 *
 * Drawn at a fixed 220×74 and scaled, so it stays crisp on any width. The truck is one `<g>` moved
 * by a single transform — animating one element rather than eight keeps this at compositor level
 * instead of repainting the page behind the login form.
 */
export function RoadFooter({ className = '' }: { className?: string }) {
  return (
    <div className={`lg-road ${className}`} aria-hidden="true">
      <div className="lg-road-line" />
      <div className="lg-truck-track">
        <svg viewBox="0 0 220 74" className="lg-truck" role="img">
          {/* trailer */}
          <rect x="8" y="14" width="120" height="42" rx="5" fill="currentColor" opacity="0.92" />
          <rect x="16" y="22" width="42" height="12" rx="2" fill="#fff" opacity="0.35" />
          <rect x="16" y="38" width="26" height="8" rx="2" fill="#fff" opacity="0.22" />
          {/* cab */}
          <path d="M128 24 h30 l24 20 v12 h-54 z" fill="currentColor" />
          <path d="M133 28 h22 l17 15 h-39 z" fill="#fff" opacity="0.45" />
          {/* chassis */}
          <rect x="8" y="56" width="174" height="5" rx="2.5" fill="currentColor" opacity="0.75" />
          {/* wheels */}
          <g className="lg-wheel" style={{ transformOrigin: '40px 62px' }}>
            <circle cx="40" cy="62" r="10" fill="#0f172a" />
            <circle cx="40" cy="62" r="4" fill="#e2e8f0" />
            <rect x="39" y="53" width="2" height="18" fill="#94a3b8" opacity="0.8" />
          </g>
          <g className="lg-wheel" style={{ transformOrigin: '96px 62px', animationDelay: '-0.15s' }}>
            <circle cx="96" cy="62" r="10" fill="#0f172a" />
            <circle cx="96" cy="62" r="4" fill="#e2e8f0" />
            <rect x="95" y="53" width="2" height="18" fill="#94a3b8" opacity="0.8" />
          </g>
          <g className="lg-wheel" style={{ transformOrigin: '166px 62px', animationDelay: '-0.3s' }}>
            <circle cx="166" cy="62" r="10" fill="#0f172a" />
            <circle cx="166" cy="62" r="4" fill="#e2e8f0" />
            <rect x="165" y="53" width="2" height="18" fill="#94a3b8" opacity="0.8" />
          </g>
          {/* exhaust puffs */}
          <circle cx="6" cy="18" r="4" fill="#fff" opacity="0.25" className="lg-puff" />
          <circle cx="6" cy="18" r="3" fill="#fff" opacity="0.2" className="lg-puff" style={{ animationDelay: '0.7s' }} />
        </svg>
      </div>
    </div>
  );
}
